/**
 * @author Nerius Ilmonas
 * @version 2025-08-22
 */

import { useEffect } from 'react';

import { fetchGetSellerWhitelabelling } from '@iwoca/lapi-client/iwocapay';
import { useQuery } from '@tanstack/react-query';

import { useGetSellerByHandle } from '../api/lending/lapiHooks';
import {
  useWhiteLabelTheme,
  type WhiteLabelTheme,
} from '../Buyer/store/WhiteLabelProvider';

/**
 * Retrieves seller white-labelling configuration.
 * @param sellerHandle The {@link https://git.iwoca.co.uk/iwoca/iwoca-django/-/blob/558575273b67e81d38f4e81ccde81797a6b4563b/iwocapay/models.py#L188 Seller.handle} to retrieve by.
 * @returns theme containing {@link WhiteLabelTheme} or error.
 * N.B. This hook can only be used within a WhiteLabelProvider.
 */
export function useSellerWhitelabelling(sellerHandle?: string): {
  theme?: WhiteLabelTheme;
  error: Error | null;
  showSellerLogo: boolean;
  isTheme: boolean;
  loadingWhitelabelling: boolean;
} {
  const { seller, loadingSeller } = useGetSellerByHandle(sellerHandle);

  const {
    setTheme,
    theme,
    showSellerLogo,
    isTheme,
    setIsThemeResolved,
    isThemeResolved,
    isWhiteLabelPreview,
  } = useWhiteLabelTheme();

  const applyTheme = !!seller?.pilots?.white_labelled || isWhiteLabelPreview;

  const { data, error, isPending } = useQuery({
    queryKey: ['fetchGetSellerWhitelabelling', sellerHandle],
    queryFn: () =>
      fetchGetSellerWhitelabelling({ sellerHandle: sellerHandle! }),
    enabled: !!sellerHandle && applyTheme,
  });

  useEffect(() => {
    // Apply the theme when the white_labelled pilot is on, or when previewing.
    if (applyTheme && data?.data && !error) setTheme(data.data);

    if ((!loadingSeller && !isPending) || (!loadingSeller && !applyTheme))
      setIsThemeResolved(true);
  }, [
    applyTheme,
    data?.data,
    error,
    isPending,
    loadingSeller,
    setTheme,
    setIsThemeResolved,
  ]);

  return {
    theme,
    error,
    showSellerLogo,
    isTheme,
    loadingWhitelabelling: !isThemeResolved,
  };
}
